// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module createPageView
 * @package @geenius/analytics/solidjs-css
 * @description Tracks page-view transitions for route-level SolidJS CSS
 * components. Static URLs emit once on mount, while accessor URLs emit when the
 * tracked route value changes.
 */

import { createEffect, onCleanup, onMount, type Accessor } from "solid-js";
import { createAnalytics } from "./createAnalytics";

export interface TanStackRouterLocationLike {
  href?: string;
  pathname?: string;
  searchStr?: string;
  hash?: string;
  publicHref?: string;
}

export interface TanStackRouterNavigationEventLike {
  toLocation?: TanStackRouterLocationLike;
}

export interface TanStackRouterLike {
  state?: {
    location?: TanStackRouterLocationLike;
  };
  subscribe?: (
    eventType: "onResolved" | "onLoad",
    listener: (event: TanStackRouterNavigationEventLike) => void,
  ) => () => void;
}

export type PageViewRouteProperties =
  | Record<string, unknown>
  | ((location: TanStackRouterLocationLike | null) => Record<string, unknown>);

export interface CreatePageViewOptions {
  url?: string | Accessor<string | null | undefined>;
  properties?: PageViewRouteProperties;
  router?: TanStackRouterLike | null;
  routerEvent?: "onResolved" | "onLoad";
  getUrl?: (location: TanStackRouterLocationLike) => string;
}

export type PageViewUrlInput =
  | string
  | Accessor<string | null | undefined>
  | CreatePageViewOptions;

function resolveRouterUrl(
  locationLike: TanStackRouterLocationLike | null | undefined,
  getUrl?: (location: TanStackRouterLocationLike) => string,
): string | null {
  if (!locationLike) return null;
  const customUrl = getUrl?.(locationLike)?.trim();
  if (customUrl) return customUrl;
  const href = locationLike.publicHref ?? locationLike.href;
  if (href?.trim()) return href;
  const pathname = locationLike.pathname?.trim();
  if (!pathname) return null;
  const search = locationLike.searchStr ?? "";
  const rawHash = locationLike.hash?.trim() ?? "";
  const hash = rawHash
    ? rawHash.startsWith("#")
      ? rawHash
      : `#${rawHash}`
    : "";
  return `${pathname}${search}${hash}`;
}

function resolvePageViewUrl(
  url?: string | null,
  locationLike?: TanStackRouterLocationLike | null,
  getUrl?: (location: TanStackRouterLocationLike) => string,
): string | null {
  const explicitUrl = url?.trim();
  if (explicitUrl) return explicitUrl;

  const routerUrl = resolveRouterUrl(locationLike, getUrl);
  if (routerUrl) return routerUrl;

  if (typeof location !== "undefined" && location.href.trim()) {
    return location.href;
  }

  return null;
}

function resolvePageViewProperties(
  properties: PageViewRouteProperties | undefined,
  locationLike: TanStackRouterLocationLike | null,
): Record<string, unknown> | undefined {
  if (!properties) return undefined;
  return typeof properties === "function"
    ? properties(locationLike)
    : properties;
}

function normalizePageViewInput(
  input?: PageViewUrlInput,
): CreatePageViewOptions {
  if (typeof input === "string" || typeof input === "function") {
    return { url: input };
  }

  return input ?? {};
}

/**
 * Records a page-view event on mount and when a reactive route URL changes.
 *
 * @param url Optional explicit URL or Solid accessor. Defaults to `location.href` in the browser.
 * @returns Nothing. The primitive records page-view side effects only.
 *
 * @example
 * ```tsx
 * function DashboardRoute() {
 *   createPageView('/analytics/dashboard')
 *   return <Dashboard />
 * }
 * ```
 */
export function createPageView(input?: PageViewUrlInput): void {
  const analytics = createAnalytics();
  const options = normalizePageViewInput(input);
  let lastTrackedUrl: string | null = null;
  const trackNext = (
    candidate?: string | null,
    locationLike: TanStackRouterLocationLike | null = null,
  ): void => {
    const nextUrl = resolvePageViewUrl(candidate, locationLike, options.getUrl);
    if (!nextUrl || lastTrackedUrl === nextUrl) return;

    lastTrackedUrl = nextUrl;
    void analytics.page(
      nextUrl,
      resolvePageViewProperties(options.properties, locationLike),
    );
  };

  if (typeof options.url === "function") {
    const urlAccessor = options.url;
    createEffect(() => {
      trackNext(urlAccessor());
    });
    return;
  }

  onMount(() => {
    const configuredUrl =
      typeof options.url === "string" ? options.url : undefined;
    const initialLocation = options.router?.state?.location ?? null;
    trackNext(configuredUrl, initialLocation);

    const unsubscribe = options.router?.subscribe?.(
      options.routerEvent ?? "onResolved",
      (event) => {
        trackNext(
          configuredUrl,
          event.toLocation ?? options.router?.state?.location ?? null,
        );
      },
    );

    onCleanup(() => {
      unsubscribe?.();
    });
  });
}
